import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { Box, Button, Grid, Typography } from '@mui/material';
import { UserActivityService } from "../../../../Component/Api/UserActivity/UserActivityService";
import { DataTable } from "../../../../Component/Common/Components/DataTable/DataTable";
import { FilterType, IFilter } from "../../../../Component/Common/Components/DataTable/Filters";
import { MDAlert } from '../../../../Component/Common/Components/MDAlert';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDProgress } from '../../../../Component/Common/Components/MDProgress';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { ColumnSortOption, SortDirections } from "../../../../Component/Model/Dto/ColumnSortOption";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../Component/Model/helpers";
import { userRegistrationIdState, userRolesState, UserAtom } from "../../../../State/user.Atom";
import { UserAdminRole, SubAdminRole } from "../../../../Component/Common/Constants/UserRoles";
import { UserActivityTrackingTableMapper } from "./UserActivityTrackingTableColumn";
import { UserActivityDto, UserActivitySearchRequest } from "../../../../Component/Model/Dto/UserActivity";
import { scorecardResponse } from "../../../../Component/Model/Dto/ScorecardLicense";
import { BEEScorecardService } from "../../../../Component/Api/BEEScorecard/BEEScorecard";
import { UserActivityActionType, DataImportType } from "../../../../Component/Model/Enum/BEE123Enums";
import { useSignalRConnection } from '../../../../Hooks/useSignalRConnection';

export const UserActivityTracking = () => {
  const userRoles = useRecoilValue(userRolesState);
  const userRegistrationId = useRecoilValue(userRegistrationIdState);
  const user = useRecoilValue(UserAtom);
  const hub = useSignalRConnection("ImportHub");

  const [activityData, setActivityData] = useState<UserActivityDto[]>(defaultObject);
  const [scorecards, setScorecards] = useState<scorecardResponse[]>([]);
  const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchValue, setSearchValue] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [selectedScorecardIds, setSelectedScorecardIds] = useState<string[]>([]);
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const [fileExporting, setFileExporting] = useState<boolean>(false);

  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({
    defaultValue: 10,
  });

  useEffect(() => {
    if (!userRoles.includes(UserAdminRole)) {
      toast.error("You do not have permission to access this page");
      return;
    }
  }, [userRoles]);

  const loadScorecards = async () => {
    try {
      const response = await BEEScorecardService.getAllScorecards();
      const allScorecards = [
        ...(response.data.beeScorecards || []),
        ...(response.data.eeScorecards || [])
      ];
      setScorecards(allScorecards);
    } catch (error) {
      console.error("Error loading scorecards:", error);
      toast.error("Failed to load scorecards");
    }
  };

  useEffect(() => {
    loadScorecards();
  }, []);

  const searchActivityData = async () => {
    if (!userRegistrationId) {
      return;
    }

    setIsLoading(true);
    try {
      const searchRequest: UserActivitySearchRequest = {
        registrationId: userRegistrationId,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        startDate: startDate,
        endDate: endDate,
        activityTypeIds: selectedActivityTypes.length > 0 ? selectedActivityTypes : undefined,
        scorecardIds: selectedScorecardIds.length > 0 ? selectedScorecardIds : undefined,
        columnSortOptions: columnSortOptions
      };

      const response = await UserActivityService.searchUserActivity(searchRequest);
      setActivityData(response.data.data);
      setFilteredItemsCount(response.data.filteredItemsCount);
      setTotalItemsCount(response.data.totalItemsCount);
    } catch (error) {
      console.error("Error searching user activity:", error);
      toast.error("Failed to load user activity data");
      setActivityData(defaultObject);
      setFilteredItemsCount(0);
      setTotalItemsCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userRegistrationId) {
      searchActivityData();
    }
  }, [
    entriesPerPage,
    pageNumber,
    columnSortOptions,
    searchValue,
    startDate,
    endDate,
    selectedScorecardIds,
    selectedActivityTypes,
    userRegistrationId,
  ]);

  useEffect(() => {
    (async () => {
      try {
        if (!hub.isConnected) {
          await hub.start();
        }

        hub.on(`exportProgress-${user.username}-${DataImportType[DataImportType.UserTracking]}`, (data: any) => {
          setFileExporting(true);
        });

        hub.on(`exportProgressUpdate-${user.username}-${DataImportType[DataImportType.UserTracking]}`, (data: any) => {
          setProgress(data.progress);
          setFileExporting(true);
        });

        hub.on(`exportProgressFailed-${user.username}-${DataImportType[DataImportType.UserTracking]}`, (data: any) => {
          setFileExporting(false);
          toast.error("Export could not be completed");
        });

        hub.on(`exportProgressCompleted-${user.username}-${DataImportType[DataImportType.UserTracking]}`, (data: any) => {
          setFileExporting(false);
          setProgress(0);
          window.open(data.ExportUrl, "_blank");
        });
      } catch (error) {
        console.error("SignalR connection failed:", error);
      }
    })();
  }, []);

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (updatedPageNumber: number) => {
    setPageNumber(updatedPageNumber);
  };

  const searchValueChanged = (value: string) => {
    setSearchValue(value);
    setPageNumber(1);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions([sortOption]);
    setPageNumber(1);
  };

  const filtersChanged = async (filterProps: any) => {
    const { start, end } = filterProps['dateRange'] || { start: null, end: null };
    const scorecardIds = filterProps['scorecardIds'] || [];
    const activityTypes = filterProps['activityTypes'] || [];

    setStartDate(start);
    setEndDate(end);
    setSelectedScorecardIds(scorecardIds);
    setSelectedActivityTypes(activityTypes);
    setPageNumber(1);
  };

  const onExportData = async () => {

    const exportRequest: UserActivitySearchRequest = {
      registrationId: userRegistrationId,
      searchValue: searchValue,
      startDate: startDate,
      endDate: endDate,
      activityTypeIds: selectedActivityTypes.length > 0 ? selectedActivityTypes : undefined,
      scorecardIds: selectedScorecardIds.length > 0 ? selectedScorecardIds : undefined,
      pageNumber: 1,
      columnSortOptions: columnSortOptions
    };

    setFileExporting(true);
    try {
      await UserActivityService.exportSearchResults(exportRequest);
    } catch (error) {
      console.log(error);
      toast.error("Export could not be completed");
      setFileExporting(false);
    }
  };

  const activityFilters: Array<IFilter> = [
    {
      label: "Activity Type:",
      values: Object.entries(UserActivityActionType)
        .filter(([key, value]) => typeof value === 'number')
        .map(([key, value]) => ({
          key: key,
          val: value
        })),
      type: FilterType.multiselect,
      propname: "activityTypes",  
    },
    {
      label: "Date Range:",
      type: FilterType.dateRange,
      propname: "dateRange",
    },
    {
      label: "Scorecard:",
      values: scorecards.map((sc) => ({ key: sc.scorecardName, val: sc.scorecardId })),
      type: FilterType.multiselect,
      propname: "scorecardIds",
    },
  ];

  const tableData = UserActivityTrackingTableMapper(activityData);

  return (
    <>
      <DataTable
        key={totalItemsCount}
        table={tableData}
        canSearch
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemsCount}
        totalItemsCount={totalItemsCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        searchValueChanged={searchValueChanged}
        isLoading={isLoading}
        manualSorting={true}
        columnSortingChanged={columnSortingChanged}
        filters={activityFilters}
        filtersChanged={filtersChanged}
      />

      <Grid container>
        <Grid item xs={12} lg={12}>
          <MDBox mt={4}>
            <MDAlert color='light'>
              {fileExporting ?
                <Grid container justifyContent="center" alignItems="center" direction="column">
                  <Typography variant="h6" mb={1}>Exporting file...</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <MDProgress variant="determinate" value={progress} style={{ width: '100%' }} />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
                    </Box>
                  </Box>
                </Grid> :
                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                  <p style={{ marginBottom: "0.2rem" }}> Click
                    <Button variant="text" onClick={onExportData} size="small"
                      style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>
                      here
                    </Button>
                    to Export data to a file
                  </p>
                </MDTypography>
              }
            </MDAlert>
          </MDBox>
        </Grid>
      </Grid>
    </>
  );
};
